import { useContext } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { apiGet, apiPost, apiPatch, apiDelete } from 'api'
import AuthContext from 'context/auth-context'

// Datum tag management for municipal user admins (requires is_user_admin).
// Tags are scoped server-side to the caller's municipality.

const TAGS_KEY = ['municipality-admin-datum-tags']

const getTags = async (token) => apiGet({ url: 'municipality_admin/datum_tags', token })

const createTag = async (token, params) =>
  apiPost({ url: 'municipality_admin/datum_tags', token, objectName: 'datum_tag', params })

const updateTag = async (token, tagId, params) =>
  apiPatch({ url: `municipality_admin/datum_tags/${tagId}`, token, objectName: 'datum_tag', params })

const deleteTag = async (token, tagId) =>
  apiDelete({ url: `municipality_admin/datum_tags/${tagId}`, token })

const getReports = async (token) => apiGet({ url: 'municipality_admin/datum_reports', token })

export const useMunicipalityAdminDatumTags = ({ options } = {}) => {
  const token = useContext(AuthContext)
  return useQuery(TAGS_KEY, () => getTags(token), options)
}

// Full Power BI workspace catalog (not filtered by the caller's own access).
export const useMunicipalityAdminDatumReports = ({ options } = {}) => {
  const token = useContext(AuthContext)
  return useQuery(['municipality-admin-datum-reports'], () => getReports(token), options)
}

const useInvalidatingMutation = (mutationFn, options) => {
  const queryClient = useQueryClient()
  return useMutation(mutationFn, {
    ...options,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(TAGS_KEY)
      queryClient.invalidateQueries(['municipality-admin-users'])
      options?.onSuccess?.(data, variables)
    }
  })
}

export const useCreateMunicipalityAdminDatumTag = ({ options } = {}) => {
  const token = useContext(AuthContext)
  return useInvalidatingMutation((params) => createTag(token, params), options)
}

export const useUpdateMunicipalityAdminDatumTag = ({ options } = {}) => {
  const token = useContext(AuthContext)
  return useInvalidatingMutation(({ tagId, params }) => updateTag(token, tagId, params), options)
}

export const useDeleteMunicipalityAdminDatumTag = ({ options } = {}) => {
  const token = useContext(AuthContext)
  return useInvalidatingMutation((tagId) => deleteTag(token, tagId), options)
}
